import { authStore } from "../auth/auth-store";

/**
 * Creates a user-specific storage wrapper around localStorage.
 * This wrapper prefixes all storage keys with the current user's ID,
 * ensuring that different users have isolated storage spaces.
 *
 * @returns An object with methods to interact with user-specific localStorage
 */
export const createUserStorage = () => {
  /**
   * Builds the user-scoped key. auth-service login() sets user.id = "" until
   * syncUserData() fills it in, so the id has to be treated as absent while it
   * is empty; otherwise that window writes a dangling "key:" entry.
   */
  const userKey = (key: string) => `${key}:${authStore.getState().user?.id || "anonymous"}`;

  return {
    /**
     * Retrieves an item from localStorage with user-specific prefix
     * @param key - The storage key to retrieve
     * @returns The stored value or null if not found
     */
    getItem: (key: string) => localStorage.getItem(userKey(key)),

    /**
     * Stores an item in localStorage with user-specific prefix
     * @param key - The storage key to set
     * @param value - The value to store
     */
    setItem: (key: string, value: string) => {
      localStorage.setItem(userKey(key), value);
    },

    /**
     * Removes an item from localStorage with user-specific prefix
     * @param key - The storage key to remove
     */
    removeItem: (key: string) => {
      localStorage.removeItem(userKey(key));
    },
  };
};
